// When the browse pop-up fires. Three rules matter more than the trigger
// itself, all enforced here:
//   1. Once per person: a shown or dismissed prompt is remembered in
//      localStorage and never fires again.
//   2. Never on a visit that arrived from an email: the drip and the pop-up
//      must not fight over the same person.
//   3. Time on page (default 30s) rather than click-counting or exit intent:
//      least annoying, easiest to reason about.
//
// The timer only makes the prompt *due*. It is marked as seen when the dialog
// is actually on screen (markShown, called by QuizModal once the quiz data has
// loaded), so a slow phone that never loads the data is not burned.
import { useCallback, useEffect, useState } from "react";
import { PROFILE_KEY } from "./useQuizData";

export const SEEN_KEY = "af_copilot_prompted";

export function fromEmail(): boolean {
  try {
    const p = new URLSearchParams(window.location.search);
    const medium = (p.get("utm_medium") ?? "").toLowerCase();
    return medium === "email" || p.has("from_email") || p.get("utm_source") === "resend";
  } catch {
    return false;
  }
}

export function alreadyPrompted(): boolean {
  try {
    if (localStorage.getItem(SEEN_KEY)) return true;
    // Someone who already finished the quiz is never prompted, even if the
    // prompt marker was cleared: they have nothing to be sold.
    if (localStorage.getItem(PROFILE_KEY)) return true;
    return false;
  } catch {
    return true; // no storage means no way to honour "once", so never fire
  }
}

export function useQuizTrigger(delayMs = 30_000) {
  const [due, setDue] = useState(false);

  useEffect(() => {
    if (alreadyPrompted() || fromEmail()) return;
    const t = setTimeout(() => {
      // Re-check: another tab may have shown it while this one waited.
      if (!alreadyPrompted()) setDue(true);
    }, delayMs);
    return () => clearTimeout(t);
  }, [delayMs]);

  const markShown = useCallback(() => {
    try {
      localStorage.setItem(SEEN_KEY, new Date().toISOString());
    } catch {
      // storage refused after the check above; the prompt still closes normally
    }
  }, []);
  const close = useCallback(() => setDue(false), []);
  return { due, markShown, close };
}
